import { useMemo, useRef } from 'react';
import { useMarketPolicy } from '~/components/Policy';

export const currencySymbol = {
  USD: '$',
  KRW: '₩',
  JPY: '¥',
  TWD: 'NT$',
};

export const currencyTickerWithSymbol = {
  USD: 'USD($)',
  KRW: 'KRW(₩)',
  JPY: 'JPY(¥)',
  TWD: 'NTD(NT$)',
};

const formatToParts = (value: number | bigint, options: Intl.NumberFormatOptions) => {
  let currencyFormatter;
  let supported = true;

  try {
    currencyFormatter = new Intl.NumberFormat('en-US', {
      ...options,
      style: 'currency',
    });
  } catch {
    supported = false;
    currencyFormatter = new Intl.NumberFormat('en-US', {
      ...options,
      currency: 'USD',
      style: 'currency',
    });
  }

  const parts = currencyFormatter.formatToParts(value);
  const i = parts.findIndex(({ type }) => type === 'currency');

  if (!supported) {
    // NOTE update value in mutable way for convenience
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    parts[i].value = options.currency!;
  }

  return parts;
};

export const useCurrency = (value: number | bigint, options?: Intl.NumberFormatOptions) => {
  const optionsRef = useRef(options);
  const policy = useMarketPolicy(options?.currency || '');
  optionsRef.current = {
    maximumFractionDigits: policy.data?.maxDecimalPoint,
    ...options,
  };

  return useMemo(() => {
    const parts = formatToParts(value, {
      currencyDisplay: 'code',
      ...optionsRef.current,
      style: 'currency',
    });
    const currencyPart = parts.find(({ type }) => type === 'currency');
    const formattedNumber = parts
      .filter(({ type }) => type !== 'currency' && type !== 'literal')
      .map((part) => part.value)
      .join('');

    return { currency: currencyPart?.value, formatted: formattedNumber, parts };
  }, [value]);
};

export type Currency =
  | 'KAIA'
  | 'KLAY'
  | 'FNSA'
  | 'ETH'
  | 'USD'
  | 'KRW'
  | 'JPY'
  | 'TWD'
  | 'THB'
  | string;
